/** @jsx jsx */
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  IconButton,
  List,
  ListLabel,
} from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import {
  GraphQLInterfaceType,
  GraphQLObjectType,
  GraphQLSchema,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback, useMemo } from 'react';

import {
  Directive,
  getDirectiveKey,
} from 'src/app/graph/documentationPage/schemaPage/directive/Directive';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import {
  GraphQLArgumentNode,
  GraphQLNode,
  getRootTypeNames,
} from 'src/lib/graphql/graphqlHelpers';
import { GetIsEntity } from 'src/lib/graphql/subgraphMetadataHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { DescriptionMarkdownRenderer } from '../../documentationPage/schemaPage/descriptionMarkdownRenderer/DescriptionMarkdownRenderer';
import { GraphRef } from '../../hooks/useGraphRef';
import { SensiblyBreakCamelCase } from '../components/sensiblyBreakCamelCase/SensiblyBreakCamelCase';
import { StickyHeader } from '../components/stickyHeader/StickyHeader';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import { EditorBottomTab } from '../helpers/EditorBottomTab';
import {
  isGraphqlField,
  typeMapWithoutInternal,
} from '../helpers/graphqlHelpers';
import {
  OperationPath,
  graphQLNodeToOperationPathSegment,
  operationPathToGraphQLPath,
} from '../helpers/operationPathHelpers';
import { useAnalyticsContext } from '../hooks/useAnalyticsContext';
import { useDerivedEditorValue } from '../hooks/useDerivedEditorValue';
import { DocsNavigationAction } from '../hooks/useExplorerState/useDocsPath';

import { EntityIcon } from './EntityIcon';
import { FieldToggle } from './fieldToggle/FieldToggle';
import { InputField } from './inputField/InputField';
import { NodeRenderer } from './NodeRenderer';
import { useFieldToggle } from './useFieldToggle';

interface ExplorerDocsProps {
  graphRef: GraphRef | null;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  docsPath: OperationPath;
  dispatchDocsNavAction: React.Dispatch<DocsNavigationAction>;
  schema: GraphQLSchema;
  createdAt: string | number;
  hash: string;
  setActiveEditorBottomTab: (tab: EditorBottomTab) => void;
  shouldManageVariables: boolean;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  openSearch: Function;
  isSupergraph: boolean;
  getIsEntity: GetIsEntity | undefined;
}

export const ExplorerDocs = ({
  graphRef,
  editor,
  docsPath: operationPath,
  dispatchDocsNavAction: dispatchNavAction,
  schema,
  createdAt,
  hash,
  variablesEditor,
  setActiveEditorBottomTab,
  shouldManageVariables,
  explorerBehavioralFlags,
  openSearch,
  isSupergraph,
  getIsEntity,
}: ExplorerDocsProps) => {
  const { track } = useAnalyticsContext();
  const { variablesEditorText } = useDerivedEditorValue({
    editor: variablesEditor,
    getValue: useCallback(
      (text: string, position: monacoEditor.Position | undefined) => ({
        variablesEditorPosition: position,
        variablesEditorText: text,
      }),
      [],
    ),
    withPosition: true,
  });
  const typeMap = useMemo(
    () => typeMapWithoutInternal(schema, isSupergraph),
    [schema, isSupergraph],
  );

  const path = useMemo(
    () => operationPathToGraphQLPath(operationPath, schema) ?? [],
    [operationPath, schema],
  );
  React.useEffect(() => {
    // If we have an operationPath but no path, the schema has
    // updated to a state where our path no longer matches the schema.
    // Reset the path.
    if (operationPath.length && !path.length) {
      dispatchNavAction({
        type: 'navigate to',
        path: [],
      });
    }
  }, [dispatchNavAction, operationPath, path]);
  const currentNode = path.slice(-1)[0] ?? null;
  const rootTypeNames = React.useMemo(() => getRootTypeNames(schema), [schema]);

  const toggleEditorContext = React.useMemo(
    () => ({
      editor,
      schema,
      variablesEditor,
      setActiveEditorBottomTab,
      shouldManageVariables,
    }),
    [
      editor,
      schema,
      variablesEditor,
      setActiveEditorBottomTab,
      shouldManageVariables,
    ],
  );
  const fieldToggle = useFieldToggle(path, toggleEditorContext);

  const [fieldPath, argumentPath] = useMemo(() => {
    const indexOfFirstArgumentNode = path.findIndex(
      (node) => !isGraphqlField(node),
    );

    if (indexOfFirstArgumentNode === -1) {
      return [path, []];
    }
    return [
      path.slice(0, indexOfFirstArgumentNode),
      // Items after the first argument should be of type GraphQLArgumentNode
      path.slice(indexOfFirstArgumentNode) as GraphQLArgumentNode[],
    ];
  }, [path]);

  const parentFieldPath = React.useMemo(
    () => (argumentPath.length ? fieldPath : fieldPath.slice(0, -1)),
    [argumentPath.length, fieldPath],
  );
  const parentFieldToggleBase = useFieldToggle(
    parentFieldPath,
    toggleEditorContext,
  );

  // For adding this node, use the parent context
  const parentFieldToggleWithContext = useMemo(() => {
    // This is required to make sure it's regenerated when variables text change.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    variablesEditorText;

    const isArgumentNode =
      currentNode?.astNode?.kind === 'InputValueDefinition';

    if (
      !parentFieldToggleBase ||
      !currentNode ||
      (!isArgumentNode && !isGraphqlField(currentNode))
    ) {
      return;
    }

    return {
      ...parentFieldToggleBase,
      isInOperation: () => {
        if (isGraphqlField(currentNode)) {
          return parentFieldToggleBase.isInOperation(currentNode);
        }
        return parentFieldToggleBase.isArgumentPathValueSet(argumentPath);
      },
      toggleInOperation: () => {
        if (isGraphqlField(currentNode)) {
          return parentFieldToggleBase.toggleInOperation(currentNode);
        }
        return parentFieldToggleBase.toggleArgumentPathInOperation(
          argumentPath,
        );
      },
      insertAllFieldsRecursively: () => {
        if (isGraphqlField(currentNode)) {
          return parentFieldToggleBase.insertAllFieldsRecursively(
            currentNode,
            true,
          );
        }
      },
    };
  }, [parentFieldToggleBase, argumentPath, currentNode, variablesEditorText]);

  const [sortOrder, setSortOrder] = useLocalStorage(
    'explorerDocsFieldSortOrder',
  );

  const onSelectInput = React.useCallback(
    (selectedNode: GraphQLArgumentNode) => {
      dispatchNavAction({
        type: 'step down to',
        node: graphQLNodeToOperationPathSegment(
          selectedNode,
          rootTypeNames,
          false,
          true,
        ),
      });
    },
    [dispatchNavAction, rootTypeNames],
  );

  const onSelectNode = React.useCallback(
    (node: GraphQLNode) =>
      dispatchNavAction({
        type: 'step down to',
        node: graphQLNodeToOperationPathSegment(
          node,
          rootTypeNames,
          !currentNode,
          false,
        ),
      }),
    [currentNode, dispatchNavAction, rootTypeNames],
  );
  const isDeprecated =
    currentNode && 'isDeprecated' in currentNode && currentNode.isDeprecated;
  const fieldDirectives = currentNode?.astNode?.directives?.filter(
    (directive) => !isDeprecated || directive.name.value !== 'deprecated',
  );

  return (
    <React.Fragment>
      <StickyHeader className="flex justify-between">
        Documentation
        {explorerBehavioralFlags.showOnlyDocumentationTab && (
          <Button
            variant="hidden"
            size="sm"
            onClick={() => openSearch()}
            leftIcon={<IconSearch />}
          >
            Search
          </Button>
        )}
      </StickyHeader>
      <Breadcrumb
        className="mb-8 mt-1 flex flex-wrap items-center"
        separatorType="chevron"
        css={css`
          ol {
            flex-wrap: wrap;
          }
        `}
      >
        {[{ name: 'Root' }, ...path].map((node, j) => {
          const i = j - 1; // we prepended the root node, but i to be used intuitively still
          return (
            <BreadcrumbItem
              className={classnames(
                'mt-1 flex cursor-pointer items-center hover:underline',
                i >= fieldPath.length && 'italic',
              )}
              key={[{ name: 'Root' }, ...path]
                .slice(0, i)
                .map((n) => n.name)
                .join('-')}
              onClick={() => {
                track('Click Doc Path');
                dispatchNavAction({
                  type: 'go up to',
                  index: i,
                });
              }}
              isCurrentPage={i === path.length - 1}
            >
              <BreadcrumbLink className="text-sm font-normal">
                <SensiblyBreakCamelCase
                  camelCasedString={
                    (node instanceof GraphQLObjectType ||
                      node instanceof GraphQLInterfaceType) &&
                    i !== 0
                      ? `on ${node.name}`
                      : i === fieldPath.length
                        ? `(${node.name})`
                        : node.name
                  }
                />
                {'type' in node && getIsEntity?.(node.type) && (
                  <EntityIcon className="ml-1" size="small" />
                )}
              </BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
      {currentNode && (
        <React.Fragment>
          <div className="flex items-start gap-x-2 text-lg font-semibold">
            <div
              className="mt-0.5"
              style={{
                // there is no tailwind rule for just this :(
                // .break-words is actually not the same thing
                wordBreak: 'break-word',
              }}
            >
              <SensiblyBreakCamelCase camelCasedString={currentNode.name} />
              {'type' in currentNode && (
                <React.Fragment>
                  :{' '}
                  <span className="inline-flex items-center text-secondary">
                    {currentNode.type.toString()}{' '}
                    {getIsEntity?.(currentNode.type) && (
                      <EntityIcon className="ml-1" size="small" />
                    )}
                  </span>
                </React.Fragment>
              )}
            </div>
            {parentFieldToggleWithContext && (
              <IconButton
                aria-label="Toggle field to be checked or unchecked"
                size="sm"
                variant="hidden"
                icon={
                  <FieldToggle
                    checked={
                      parentFieldToggleWithContext.isInOperation()
                        ? 'checked'
                        : 'unchecked'
                    }
                  />
                }
                onClick={(event) => {
                  event.stopPropagation();
                  const wasIncluded =
                    parentFieldToggleWithContext.isInOperation();
                  if (wasIncluded) {
                    track(`Toggle Field Off`);
                  } else {
                    track(`Toggle Field On`);
                  }
                  if (!wasIncluded && (event.metaKey || event.ctrlKey)) {
                    parentFieldToggleWithContext.insertAllFieldsRecursively();
                  } else {
                    parentFieldToggleWithContext.toggleInOperation();
                  }
                }}
              />
            )}
          </div>
          {isDeprecated && (
            <Alert status="warning">
              {currentNode.deprecationReason
                ? `Deprecated: ${currentNode.deprecationReason}`
                : 'Deprecated'}
            </Alert>
          )}

          {isMinLengthArray(1, fieldDirectives) && (
            <div className="mt-1">
              {fieldDirectives.map((directive) => (
                <Directive
                  directive={directive}
                  typeMap={typeMap}
                  key={getDirectiveKey(directive)}
                />
              ))}
            </div>
          )}

          {'description' in currentNode && !!currentNode.description && (
            <div className="mt-2 text-sm text-secondary">
              <DescriptionMarkdownRenderer
                descriptionMarkdown={currentNode.description}
                typeMap={typeMap}
              />
            </div>
          )}

          {'args' in currentNode && currentNode.args.length > 0 && (
            <List isInteractive className="!gap-0">
              <ListLabel className="mt-8 flex h-9 items-center">
                Arguments
              </ListLabel>
              {currentNode.args.map((arg) => (
                <InputField
                  isNodeInOperation={fieldToggle?.isArgumentPathValueSet}
                  toggleNodeInOperation={
                    fieldToggle?.toggleArgumentPathInOperation
                  }
                  key={arg.name}
                  inputField={arg}
                  onSelect={onSelectInput}
                  shouldManageVariables={shouldManageVariables}
                />
              ))}
            </List>
          )}
        </React.Fragment>
      )}
      <NodeRenderer
        graphRef={graphRef}
        schema={schema}
        createdAt={createdAt}
        hash={hash}
        node={currentNode}
        onSelectNode={onSelectNode}
        fieldToggle={fieldToggle}
        docsPath={path}
        argumentPath={argumentPath}
        parentFieldToggle={parentFieldToggleWithContext}
        shouldManageVariables={shouldManageVariables}
        toggleEditorContext={toggleEditorContext}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        explorerBehavioralFlags={explorerBehavioralFlags}
        typeMap={typeMap}
        getIsEntity={getIsEntity}
      />
    </React.Fragment>
  );
};
